//#region import
import React, { useEffect, useState } from "react";
import {
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { FnAxios, FnDialog, I18n } from "utilities/utilities";
import DataGrid, { PaginationInterface, initRows } from "components/DataGrid";
import {
  BtnAddOnTable,
  BtnEditOnTable,
  BtnViewOnTable,
} from "components/Button/ButtonTable";
import DialogPreview from "components/DialogPreview/DialogPreview";
import { FilterFieldInterface } from "components/DataGrid/DataGridProps";
import { GridColDef } from "@mui/x-data-grid-pro";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GroupsIcon from "@mui/icons-material/Groups";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import { FormProvider, useForm } from "react-hook-form";
import { Circle } from "@mui/icons-material";
import ChangeOrder from "components/System/ChangeOrder";
import { GetMenuPermission } from "utilities/SystemFunction";
import InfoIcon from "@mui/icons-material/Info";

//#endregion

export default function PublishDocTable({ sMode }) {
  const navigate = useNavigate();
  const AxiosFn = FnAxios();
  const DialogFn = FnDialog();
  const location = useLocation();

  const [loadingTable, setLoadingTable] = useState(false);

  const [dataRow, setDataRow] = useState<PaginationInterface>({
    ...initRows,
  });

  const [nPermission, setPermission] = useState<boolean>(false); // Permission

  const [isOpenGroup, setIsOpenGroup] = useState(false);
  const [arrModalGroup, setArrModalGroup] = useState([]);
  const [isOpenPermission, setIsOpenPermission] = useState(false);
  const [objModalPermission, setObjModalPermission] = useState({
    lstPermissionRole: [],
    lstPermissionGroup: [],
    lstPermissionUser: [],
  });
  const [isOpenFile, setIsOpenFile] = useState(false);
  const [arrModalFile, setArrModalFile] = useState([]);
  const [sDocName, setDocName] = useState([]);
  const [arrOptionOrder, setArrOptionOrder] = useState([]);
  const [sUpdateName, setUpdateName] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const form = useForm({
    shouldFocusError: true,
    shouldUnregister: false,
    mode: "all",
  });

  const handleChangeOrder = (sID, nOrder) => {
    let nNewOrder = +nOrder;

    let objParam = {
      sID: sID,
      nNewOrder: nNewOrder,
      sLocation: location.pathname,
    };
    AxiosFn.Post(`PublishDoc/OnChangeOrder`, objParam, () => {
      getTable(dataRow);
    });
  };

  const getModeDocMain = () => {
    let sText = "";
    switch (sMode) {
      case "DocMain":
        sText = `${I18n.SetText("PublishDoc.sDocMain")}`;
        break;
      case "DocGroup":
        sText = `${I18n.SetText("PublishDoc.sDocGroup")}`;
        break;
      case "DocType":
        sText = `${I18n.SetText("PublishDoc.sDocType")}`;
        break;
    }
    return sText;
  };

  const getModeDocGroup = () => {
    let sText = "";
    switch (sMode) {
      case "DocMain":
        sText = `${I18n.SetText("PublishDoc.sDocGroup")}`;
        break;
      case "DocGroup":
        sText = `${I18n.SetText("PublishDoc.sDocType")}`;
        break;
      case "DocType":
        sText = `${I18n.SetText("PublishDoc.sDocMain")}/${I18n.SetText(
          "PublishDoc.sDocGroup"
        )}`;
        break;
    }
    return sText;
  };

  const handleViewInfo = (e) => {
    setUpdateName(e.sUpdateBy);
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const dataColumn: GridColDef[] = [
    {
      field: "btnAction",
      headerName: "",
      align: "center",
      type: "actions",
      resizable: false,
      sortable: false,
      width: 60,
      renderHeader: (item) =>
        nPermission ? (
          <BtnAddOnTable
            onClick={() => navigate("/PublishDocForm?sMode=" + sMode)}
            id={"btnAdd"}
          />
        ) : (
          <></>
        ),
      getActions: (item) => {
        return [
          nPermission ? (
            <BtnEditOnTable
              id="btnEdit"
              key={"btnEdit"}
              onClick={() => handleEdit(item.row.sID)}
            />
          ) : (
            <BtnViewOnTable
              id="btnEdit"
              key={"btnEdit"}
              onClick={() => handleEdit(item.row.sID)}
            />
          ),
        ];
      },
    },
    {
      field: "nOrder",
      headerName: `${I18n.SetText("PublishDoc.sOrder")}`,
      headerAlign: "center",
      align: "center",
      sortable: true,
      width: 100,
      renderCell: (item) => (
        <ChangeOrder
          item={item}
          value={item.row.nOrder}
          onChangeOrder={(sID, nOrder) => handleChangeOrder(sID, nOrder)}
          optOrder={arrOptionOrder || []}
          disabled={!nPermission}
        />
      ),
    },
    {
      field: "sTitle",
      headerName: getModeDocMain(),
      headerAlign: "center",
      align: "left",
      sortable: true,
      flex: 1,
      minWidth: 220,
    },
    {
      field: "sGroupDoc",
      headerName: getModeDocGroup(),
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: sMode === "DocType" ? 200 : 120,
      renderCell: (item) =>
        sMode === "DocType" ? (
          <>{item.row.sTitleAndGroup}</>
        ) : (
          <Tooltip title={`${I18n.SetText("PublishDoc.sDetail")}`}>
            <IconButton
              size="small"
              onClick={() => handleViewGroup(item.row.sID)}
            >
              <FormatListBulletedIcon
                style={{ color: "#6e130d", cursor: "pointer" }}
              />
            </IconButton>
          </Tooltip>
        ),
    },
    {
      field: "sPermission",
      headerName: `${I18n.SetText("PublishDoc.sPermission")}`,
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: 120,
      renderCell: (item) => (
        <Tooltip title={`${I18n.SetText("PublishDoc.sDetail")}`}>
          <IconButton
            size="small"
            onClick={() => handleViewPermission(item.row.sID)}
          >
            <GroupsIcon style={{ color: "#6e130d", cursor: "pointer" }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "objFile",
      headerName: `${I18n.SetText("PublishDoc.sFile")}`,
      headerAlign: "center",
      align: "center",
      sortable: true,
      minWidth: 120,
      renderCell: (item) => (
        <Tooltip title={`${I18n.SetText("PublishDoc.sDetail")}`}>
          <IconButton size="small" onClick={() => handleViewFile(item.row)}>
            <InsertDriveFileIcon
              style={{ color: "#6e130d", cursor: "pointer" }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "sUpdate",
      headerName: `${I18n.SetText("PublishDoc.sLastEdit")}`,
      headerAlign: "center",
      align: "center",
      sortable: true,
      minWidth: 190,
      renderCell: (item) => {
        return (
          <>
            <Grid sx={{ mr: "4px" }}>{item.row.sUpdate}</Grid>
            <IconButton
              size="small"
              onClick={(e) => {
                handleClick(e);
                handleViewInfo(item.row);
              }}
            >
              <InfoIcon style={{ color: "#6e130d", cursor: "pointer" }} />
            </IconButton>
          </>
        );
      },
    },
    {
      field: "sStatusID",
      headerName: `${I18n.SetText("PublishDoc.sStatus")}`,
      headerAlign: "center",
      align: "center",
      sortable: true,
      minWidth: 120,
      renderCell: (item) => {
        return (
          <>
            {item.row.nStatus === 1 && (
              <Tooltip title={item.row.sStatus}>
                <Chip
                  size="medium"
                  label={item.row.sStatus}
                  style={{
                    color: "rgb(22 187 68)",
                    backgroundColor: "rgb(231 255 237)",
                    margin: "0.2rem",
                    fontSize: "0.8rem",
                  }}
                />
              </Tooltip>
            )}
            {item.row.nStatus === 0 && (
              <Tooltip title={item.row.sStatus}>
                <Chip
                  size="medium"
                  label={item.row.sStatus}
                  style={{
                    color: "#f1416c",
                    backgroundColor: "#fff5f8",
                    margin: "0.2rem",
                    fontSize: "0.8rem",
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const handleEdit = (sID: string) => {
    navigate(`/PublishDocForm?sMode=${sMode}&sID=${encodeURI(sID)}`);
  };

  const handleViewGroup = (sID) => {
    let nModeGroup = sMode === "DocGroup" ? 2 : 3;
    let objParam = {
      sID: sID,
      nModeID: sMode === "DocMain" ? 1 : nModeGroup,
    };
    AxiosFn.Post(`PublishDoc/OnViewGroup`, objParam, (result) => {
      DialogFn.UnBlockUI();
      setArrModalGroup(result.lstChildMainGroupDoc);
      setIsOpenGroup(true);
    });
  };
  const handleViewPermission = (sID) => {
    let objParam = { sID: sID };
    AxiosFn.Post(`PublishDoc/OnViewPermission`, objParam, (result) => {
      DialogFn.UnBlockUI();
      setObjModalPermission(result.objPermission);
      setIsOpenPermission(true);
    });
  };
  const handleViewFile = (objRow) => {
    let objParam = { sID: objRow.sID };
    setDocName(objRow.sTitle);
    AxiosFn.Post(`PublishDoc/OnViewFile`, objParam, (result) => {
      DialogFn.UnBlockUI();
      setArrModalFile(result.lstFileDoc);
      setIsOpenFile(true);
    });
  };

  const handleDelete = (e) => {
    DialogFn.Submit(`${I18n.SetText("PublishDoc.sConfirmDel")}`, () => {
      let objParam = {
        lstID: e,
        sLocation: location.pathname,
      };
      DialogFn.BlockUI();
      AxiosFn.Post(
        `PublishDoc/OnDeleteTable`,
        objParam,
        (result) => {
          DialogFn.UnBlockUI();
          if (result.nStatusCode === 200) {
            DialogFn.Success("ลบสำเร็จ");
            getTable(dataRow);
          } else if (result.nStatusCode === 208) {
            DialogFn.Warning(
              "ไม่สามารถลบรายการได้ เนื่องจากมีการใช้งานรายการนี้อยู่"
            );
          }
        },
        (err) => {
          DialogFn.UnBlockUI();
        }
      );
    });
  };

  const getTable = (p: PaginationInterface) => {
    setLoadingTable(true);
    let nModeGroup = sMode === "DocGroup" ? 2 : 3;
    let objParam = {
      ...p,
      nModeID: sMode === "DocMain" ? 1 : nModeGroup,
    };
    AxiosFn.Post(`PublishDoc/GetTable`, objParam, (result) => {
      setLoadingTable(false);
      setDataRow({
        ...p,
        arrRows: [...result.lstData],
        nDataLength: result.ObjTable?.nDataLength,
        nPageIndex: result.ObjTable?.nPageIndex,
      });

      //Set Option Order
      let arrOption = [];
      result.lstData.forEach((item) => {
        arrOption.push({
          value: item.nOrder + "",
          label: item.nOrder + "",
        });
      });
      setArrOptionOrder([...arrOption]);
    });
  };

  useEffect(() => {
    GetMenuPermission(AxiosFn, setPermission, DialogFn);
    getTable(dataRow);
  }, []);

  //Filter Search
  const filter: FilterFieldInterface[] = [
    {
      sTypeFilterMode: "input",
      sLabel: getModeDocMain(),
      sFieldName: "sTitle",
      nGridLG: 4,
      nGridMD: 4,
      nGridSM: 6,
      nGridXS: 12,
    },
    {
      sTypeFilterMode: "select",
      sLabel: "สถานะ",
      sFieldName: "sStatusID",
      optionSelect: [
        {
          label: `${I18n.SetText("PublishDoc.sActive")}`,
          value: "1",
        },
        {
          label: `${I18n.SetText("PublishDoc.sInactive")}`,
          value: "0",
        },
      ],
      nGridLG: 2,
      nGridMD: 2,
      nGridSM: 6,
      nGridXS: 12,
    },
  ];
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormProvider {...form}>
          <DataGrid
            sTableID={`tbPublish_${sMode}`}
            columns={dataColumn}
            rows={dataRow}
            modeFilter={1}
            onLoadData={getTable}
            onDelete={handleDelete}
            isLoading={loadingTable}
            isHiddenToolHead={false}
            filterField={filter}
            isShowCheckBox={nPermission}
            onRowSelectable={(p) => p.row.isCanDel}
          />
        </FormProvider>
      </Grid>

      {/* Group */}
      <DialogPreview
        IsOpen={isOpenGroup}
        Title={getModeDocGroup()}
        required={true}
        onClose={() => setIsOpenGroup(false)}
        sMaxWidth="sm"
        CloseSave={true}
      >
        {arrModalGroup.length > 0 ? (
          <Grid item xs={12}>
            <Card sx={{ padding: 2 }}>
              {arrModalGroup.map((m) => (
                <Grid item xs={12} key={m}>
                  <span>
                    <Circle
                      sx={{ color: "#212836", height: "7px", marginLeft: 1 }}
                    />
                    {m}
                  </span>
                </Grid>
              ))}
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12} display={"flex"} justifyContent={"center"}>
            {`${I18n.SetText("PublishDoc.sNoData")}`}
          </Grid>
        )}
      </DialogPreview>

      {/* Permission */}
      <DialogPreview
        IsOpen={isOpenPermission}
        Title={`${I18n.SetText("PublishDoc.sPermission")}`}
        required={true}
        onClose={() => setIsOpenPermission(false)}
        sMaxWidth="sm"
        CloseSave={true}
      >
        {objModalPermission.lstPermissionRole.length === 0 &&
          objModalPermission.lstPermissionGroup.length === 0 &&
          objModalPermission.lstPermissionUser.length === 0 ? (
          <Grid item xs={12} display={"flex"} justifyContent={"center"}>
            {`${I18n.SetText("PublishDoc.sPublic")}`}
          </Grid>
        ) : (
          <Grid container spacing={1}>
            {/* Role */}
            {objModalPermission.lstPermissionRole.length > 0 ? (
              <Grid item xs={12}>
                <Card sx={{ padding: 2 }}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {`${I18n.SetText("PublishDoc.sRole")}`}
                    </Typography>
                  </Grid>
                  {objModalPermission.lstPermissionRole.map((m) => (
                    <Grid item xs={12} key={m}>
                      <span>
                        <Circle
                          sx={{
                            color: "#212836",
                            height: "7px",
                            marginLeft: 1,
                          }}
                        />
                        {m}
                      </span>
                    </Grid>
                  ))}
                </Card>
              </Grid>
            ) : null}

            {/* Group */}
            {objModalPermission.lstPermissionGroup.length > 0 ? (
              <Grid item xs={12}>
                <Card sx={{ padding: 2 }}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {`${I18n.SetText("PublishDoc.sUserGroup")}`}
                    </Typography>
                  </Grid>
                  {objModalPermission.lstPermissionGroup.map((m) => (
                    <Grid item xs={12} key={m}>
                      <span>
                        <Circle
                          sx={{
                            color: "#212836",
                            height: "7px",
                            marginLeft: 1,
                          }}
                        />
                        {m}
                      </span>
                    </Grid>
                  ))}
                </Card>
              </Grid>
            ) : null}

            {/* User */}
            {objModalPermission.lstPermissionUser.length > 0 ? (
              <Grid item xs={12}>
                <Card sx={{ padding: 2 }}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {`${I18n.SetText("PublishDoc.sUser")}`}
                    </Typography>
                  </Grid>
                  {objModalPermission.lstPermissionUser.map((m) => (
                    <Grid item xs={12} key={m}>
                      <span>
                        <Circle
                          sx={{
                            color: "#212836",
                            height: "7px",
                            marginLeft: 1,
                          }}
                        />
                        {m}
                      </span>
                    </Grid>
                  ))}
                </Card>
              </Grid>
            ) : null}
          </Grid>
        )}
      </DialogPreview>

      {/* File */}
      <DialogPreview
        IsOpen={isOpenFile}
        Title={`${I18n.SetText("PublishDoc.sFile")}`}
        required={true}
        onClose={() => setIsOpenFile(false)}
        sMaxWidth="sm"
        CloseSave={true}
      >
        {arrModalFile.length > 0 ? (
          <Grid item xs={12}>
            <Card sx={{ padding: 2 }}>
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: "bold" }}>{sDocName}</Typography>
              </Grid>
              {arrModalFile.map((m) => (
                <Grid key={m.sFilePath} container mt={1}>
                  <Grid item xs={12}>
                    <a
                      href={
                        process.env.REACT_APP_API_URL +
                        "UploadFile/" +
                        m.sFilePath
                      }
                      download
                    >
                      <Grid
                        item
                        xs={12}
                        container
                        justifyContent={"space-between"}
                      >
                        <Grid item key={m.sFileName}>
                          <span>
                            <Circle
                              sx={{
                                color: "#212836",
                                height: "7px",
                                marginLeft: 1,
                              }}
                            />
                            {m.sFileName}
                          </span>
                        </Grid>
                        <Grid item key={m.sFileName}>
                          <IconButton size="small">
                            <DownloadIcon color={"primary"} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </a>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12} display={"flex"} justifyContent={"center"}>
            {`${I18n.SetText("PublishDoc.sNoData")}`}
          </Grid>
        )}
      </DialogPreview>
      {/* pop over */}
      <Popover
        id={`sUpdateName`}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Typography sx={{ p: 2 }}>{sUpdateName}</Typography>
      </Popover>
    </Grid>
  );
}
